import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Table, Button } from 'reactstrap';
import { useIntl } from 'react-intl';
import _ from 'lodash';

import { showModal } from '../../../../../actions';
import { showConfirmationModal } from '../../../../../actions/App';

import { APIupdateSettings } from '../../../../../api/Settings';

import EmptyImageEditor from '../../../../../containers/EmptyContainers/ImageEditor';
import UpgradePlanButton from '../../../../UpgradePlanButton';

import { addFlashMessage } from '../../../../../helpers/FlashMessages';
import { userHasFeature } from '../../../../../helpers/Features';
import ItemImageEditorPresetRow from './ItemImageEditorPresetRow';

const SettingsItemImageEditor = ({
  settingKey,
  setting,
  handleChange,
  openEditModal,
  handleConfirmationModal,
}) => {
  const intl = useIntl();

  const presetsRef = useRef(setting.value);
  presetsRef.current = setting.value;

  const lastSavedRef = useRef(setting.value);

  const updatePresets = (settingsToUpdate) => (
    APIupdateSettings(settingsToUpdate)
  );

  const movePreset = (from, to) => {
    const next = [...presetsRef.current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    presetsRef.current = next;
    handleChange('image_editor_presets', next);
  };

  const savePresets = () => {
    const next = presetsRef.current;
    const previous = lastSavedRef.current;
    if (_.isEqual(next, previous)) return;

    updatePresets({ image_editor_presets: next })
      .then(() => {
        lastSavedRef.current = next;
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({
            defaultMessage: 'New settings will be available in Image Editor after refresh.',
          }),
        });
      })
      .catch(() => {
        presetsRef.current = previous;
        handleChange('image_editor_presets', previous);
        addFlashMessage({
          type: 'error',
          text: intl.formatMessage({ defaultMessage: 'Could not save settings.' }),
        });
      });
  };

  const setDragging = (isDragging) => {
    if (isDragging) lastSavedRef.current = presetsRef.current;
  };

  const handleSize = (presetIndex = null) => {
    openEditModal({
      className: 'modal-edit-preset',
      presets: setting.value,
      handleChange,
      updatePresets,
      settingKey,
      presetIndex,
    });
  };

  const handleDeletePreset = (index) => {
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Are you sure you want to delete this preset?' }),
      onConfirm: () => {
        const newSettings = setting.value.filter((e, i) => i !== index);
        return updatePresets({ image_editor_presets: newSettings }).then(() => {
          handleChange('image_editor_presets', newSettings);
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({
              defaultMessage: 'New settings will be available in Image Editor after refresh.',
            }),
          });
        });
      },
    };
    handleConfirmationModal(modalParams);
  };

  if (!userHasFeature('image-editor')) {
    return (
      <EmptyImageEditor />
    );
  }

  return (
    <div className="presets-list-header-row-wrapper">
      <div>
        <div className="presets-list-header">
          <h4>
            {intl.formatMessage({ defaultMessage: 'Presets list' })}
            <UpgradePlanButton feature="image-editor" />
          </h4>
          <Button color="primary" onClick={() => handleSize()} disabled={!userHasFeature('image-editor')}>
            <i className="fas fa-plus" />
            {intl.formatMessage({ defaultMessage: 'Add preset' })}
          </Button>
        </div>

        {(Array.isArray(setting.value) && setting.value.length === 0) && (
          <EmptyImageEditor handleNewItem={() => handleSize()} />
        )}
        {(Array.isArray(setting.value) && setting.value.length > 0) && (
          <div className="table-wrapper">
            <Table className="image-editor-presets" responsive>
              <thead>
                <tr>
                  <th>{' '}</th>
                  <th>{intl.formatMessage({ defaultMessage: 'Name' })}</th>
                  <th>{intl.formatMessage({ defaultMessage: 'Width (px)' })}</th>
                  <th>{intl.formatMessage({ defaultMessage: 'Height (px)' })}</th>
                  <th>{' '}</th>
                </tr>
              </thead>
              <tbody>
                {setting.value.map((v, index) => (
                  <ItemImageEditorPresetRow
                    key={`${v.label}-${v.x}-${v.y}`}
                    id={`${v.label}-${v.x}-${v.y}`}
                    index={index}
                    preset={v}
                    canDrag={userHasFeature('image-editor')}
                    moveImageEditorPreset={movePreset}
                    saveImageEditorPresets={savePresets}
                    setDragging={setDragging}
                    onEdit={handleSize}
                    onDelete={handleDeletePreset}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  openEditModal: (params) => {
    dispatch(showModal('ImageEditorPresetEditModal', params));
  },
  handleConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
});

SettingsItemImageEditor.propTypes = {
  settingKey: PropTypes.string.isRequired,
  setting: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  openEditModal: PropTypes.func.isRequired,
  handleConfirmationModal: PropTypes.func.isRequired,
};

export default connect(null, mapDispatchToProps)(SettingsItemImageEditor);
